import { useCallback, useEffect, useState } from 'react'
import { Download, Minus, Plus, Printer, RefreshCw } from 'lucide-react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button } from '@/components/ui/Button'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker

type PdfViewerProps = {
  fileName: string
  dataUrl: string
  onClose: () => void
  onDownload?: () => void
  /** When false, hide download (and print) — product view-only. Default true. */
  allowDownload?: boolean
}

export function PdfViewer({
  fileName,
  dataUrl,
  onClose,
  onDownload,
  allowDownload = true,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(1.1)
  const [reloadKey, setReloadKey] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [fileSource, setFileSource] = useState<string | { data: Uint8Array } | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setNumPages(0)
    setFileSource(null)

    async function prepare() {
      try {
        // Prefer binary copy — avoids ArrayBuffer detach + works for data:/http: URLs.
        const response = await fetch(dataUrl)
        if (!response.ok) throw new Error('Không đọc được dữ liệu PDF.')
        const buffer = await response.arrayBuffer()
        if (cancelled) return
        setFileSource({ data: new Uint8Array(buffer.slice(0)) })
      } catch (err) {
        if (cancelled) return
        // Fallback: pass data URL / http URL directly to react-pdf
        if (dataUrl.startsWith('data:') || dataUrl.startsWith('http') || dataUrl.startsWith('blob:')) {
          setFileSource(dataUrl)
          return
        }
        setError(err instanceof Error ? err.message : 'Không mở được PDF.')
      }
    }

    void prepare()
    return () => {
      cancelled = true
    }
  }, [dataUrl, reloadKey])

  const onLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
    setNumPages(total)
    setError(null)
  }, [])

  return (
    <div className="file-reader-overlay flex h-full min-h-0 min-w-0 flex-1 flex-col bg-slate-100 p-3">
      <div className="file-reader-print-root flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm">
        <div className="no-print flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate font-mono text-sm text-slate-800">{fileName}</p>
            <p className="text-xs text-slate-400">
              Cuộn chuột để xem{numPages ? ` · ${numPages} trang` : ''}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Đóng">
            [×]
          </Button>
        </div>

        <div className="no-print flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-1.5 text-xs text-slate-700">
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              aria-label="Thu nhỏ"
              onClick={() => setScale((value) => Math.max(0.5, Math.round((value - 0.1) * 10) / 10))}
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="min-w-10 text-center font-medium">{Math.round(scale * 100)}%</span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Phóng to"
              onClick={() => setScale((value) => Math.min(2.5, Math.round((value + 0.1) * 10) / 10))}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <span className="text-slate-300">|</span>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Tải lại"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {allowDownload ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => window.print()}>
                <Printer className="h-3.5 w-3.5" />
                In
              </Button>
              {onDownload ? (
                <Button size="sm" variant="ghost" onClick={onDownload}>
                  <Download className="h-3.5 w-3.5" />
                  Tải xuống
                </Button>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="file-reader-body min-h-0 flex-1 overflow-auto bg-slate-200/80">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-4 px-4 py-4">
            {error ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            ) : !fileSource ? (
              <p className="py-10 text-sm text-slate-500">Đang tải PDF trong trang...</p>
            ) : (
              <Document
                key={`${reloadKey}-${fileName}`}
                file={fileSource}
                loading={<p className="py-10 text-sm text-slate-500">Đang render PDF...</p>}
                error={
                  <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    Không hiển thị được PDF. Thử Tải lại
                    {allowDownload ? ' hoặc Tải xuống' : ''}.
                  </p>
                }
                onLoadSuccess={onLoadSuccess}
                onLoadError={(err) => setError(err.message || 'Lỗi tải PDF.')}
                externalLinkTarget="_self"
              >
                {numPages > 0
                  ? Array.from({ length: numPages }, (_, index) => (
                      <div
                        key={`page_${index + 1}`}
                        className="mb-4 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm last:mb-0"
                      >
                        <div className="border-b border-slate-100 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
                          Trang {index + 1} / {numPages}
                        </div>
                        <Page
                          pageNumber={index + 1}
                          scale={scale}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          className="mx-auto"
                          loading={
                            <div className="flex h-64 items-center justify-center text-sm text-slate-400">
                              Đang vẽ trang {index + 1}...
                            </div>
                          }
                        />
                      </div>
                    ))
                  : null}
              </Document>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
